import {
  PrefixedMetricBaseUnit,
  MetricUnit,
  SupportedRatioDenominatorUnit,
} from '@shared/models/units/metric-units.model';

import { mathJS } from '../mathjs';
import { getDisplayNumber } from './get-display-number.helper';
import { getAcceptableAutoOutputUnits } from './get-acceptable-auto-output-units.helper';
import { getMathJSUnitCategory } from '../mathjs/utils/get-mathjs-unit-category.helper';
import { UnitCategory } from '@shared/models/units/unit-base.model';
import { MathJSUnitInstance } from '../mathjs/mathjs.model';

import { tryCatch, wrapError } from '../utils.helpers';
import { UNICODE } from '@shared/models/unicode.model';
import {
  isPercentUnit,
  PercentUnit,
} from '@shared/models/units/percent-unit.model';
import { CurrencyUnit } from '@shared/models/units/currency-units.model';
import { CO2eUnit } from '@shared/models/units/co2e-units.model';
import { guards } from '../typeguards.helpers';
import { AliasUnit } from '@shared/models/units/alias-units.model';
import {
  BreakPointerClass,
  BreakPointsInstance,
} from '../breakpoints/BreakPoints';

// contains both some some mathJS units that are ugly, and also some because mathJS doesn't want to convert between for example gram and tonne
const displayUnitReplaceStrings = {
  'dm^3': 'l',
  'cm^3': 'ml',
  degC: UNICODE.UNITS.CELSIUS,
  degF: UNICODE.UNITS.FAHRENHEIT,
  degK: 'K',
} satisfies Record<string, string>;

/**
 * Converts a unit string to a display-friendly format
 * Replaces ^2 with ², ^3 with ³, and applies other unit replacements
 * @param unit - The unit string to convert (e.g., "kWh/m^2")
 * @returns The display-friendly unit string (e.g., "kWh/m²")
 */
export const toDisplayUnit = (unit: string): string => {
  let replacedUnit = String(unit);
  for (const [from, to] of Object.entries(displayUnitReplaceStrings)) {
    if (!replacedUnit.includes(from)) continue;
    replacedUnit = replacedUnit.replaceAll(from, to);
  }

  return replacedUnit
    .replaceAll('^2', '²')
    .replaceAll('^3', '³')
    .replaceAll(/\s/g, '')
    .replaceAll('*', UNICODE.MATH.MULTIPLY_DOT);
};

export type AcceptedDisplayFormatUnits =
  MetricUnit | CurrencyUnit | PercentUnit | CO2eUnit | AliasUnit;

export type SupportedDivideByUnit =
  | SupportedRatioDenominatorUnit
  | PrefixedMetricBaseUnit
  | CurrencyUnit
  | CO2eUnit
  | AliasUnit;

export type FormatInput = {
  /* Input value */
  value: number;
  /* unit of the input value */
  unit?: AcceptedDisplayFormatUnits;
};

/**
 * Output option for specific unit
 * @param unit - unit to output to
 */
type OutputOptionUnit =
  | {
      unit: AcceptedDisplayFormatUnits;
    }
  | AcceptedDisplayFormatUnits;

/**
 * Output option for best fit unit
 * @param unit - 'auto'
 * @param autoUnits - accepted conversion units for auto unit selection. If not provided, the default accepted units will be used, or any unit if no default accepted units are found.
 */
type OutputOptionAutoUnit = {
  unit: 'auto';
  autoUnits?:
    | PrefixedMetricBaseUnit[]
    | BreakPointsInstance<PrefixedMetricBaseUnit, PrefixedMetricBaseUnit>;
};

export type DivideByOption = {
  value?: number;
  unit: SupportedDivideByUnit;
};

export type FormatOutputOptions = Pretty<{
  outputUnit?: OutputOptionAutoUnit | OutputOptionUnit;
  divideBy?: DivideByOption;
  notation?: 'compact' | 'standard';
}>;

export type DisplayValueData = {
  value: string;
  valueAsNumber: number;
  unit: string;
  full: string;
  /* raw = unformatted data */
  raw: {
    value: number;
    unit: string;
  };
  error?: Error;
};

/**
 * Helper to get the display value and unit for a given input value and unit
 *
 * NOTE: If you want A SPECIFIC output unit, you MUST provide the outputUnit, otherwise the value & unit may be converted to an unintended unit.
 * examples:
 * 1. getDisplayValueData({ value: 10000, unit: 'SEK' }) will return '10 kSEK' <-- automatically k-prefixed & divided by 1000
 * 2. getDisplayValueData({ value: 10, unit: 'SEK' }) will return '10 SEK'
 *
 * @param input - the input value and unit. MUST be the unit of the value.
 * @param outputOptions - the output options
 * @param outputOptions.outputUnit - the output unit. If not provided, the function will automatically determine the best output value & unit.
 * @param outputOptions.divideBy - if you want to automatically divide the output by some value.
 * @param outputOptions.divideBy.value - optional value. default is 1. (when 1, this just appends " / <unit>" to the output units)
 * @param outputOptions.divideBy.unit - the unit to divide the input value by. If not provided, the input value will not be divided by any unit.
 * @param outputOptions.notation - the notation to use for the output. default is 'standard'. When 'compact', the unit will be omitted in the output, to avoid "10000 kSEK" => "10K kSEK".
 * @returns formatted parts of value & unit {@see Output}
 */
export const getDisplayValueData = (
  input: FormatInput,
  outputOptions?: FormatOutputOptions,
): DisplayValueData => {
  if (!input.unit) {
    // no unit, just format and return
    const value = getDisplayNumber(input.value);
    const unit = '';
    return {
      value,
      valueAsNumber: input.value,
      unit,
      full: value,
      raw: { value: input.value, unit },
    };
  }
  let outputUnit: OutputOptionUnit | OutputOptionAutoUnit =
    outputOptions?.outputUnit ?? { unit: 'auto' };

  const notation = outputOptions?.notation ?? 'standard';

  // make sure to turn off auto unit selection when value is 0
  if (input.value === 0) {
    if (guards.is.string(outputUnit)) {
      outputUnit = { unit: outputUnit };
    } else if (outputUnit.unit === 'auto') {
      outputUnit = { unit: input.unit };
    }
  }

  // don't divide by zero
  const divideByZero = outputOptions?.divideBy?.value === 0;
  const divideBy = divideByZero ? undefined : outputOptions?.divideBy;

  try {
    if (isPercentUnit(input.unit)) {
      // need to handle this specifically.
      // can't figure out a reasonable way to handle percent factor (default storage 1 ==> 100%) conversions to human readable percent.
      // so we just assume input is in factor format, and output as human readable percent.
      const value = input.value * 100;
      const displayValue = getDisplayNumber(value, { notation });
      return {
        value: displayValue,
        valueAsNumber: value,
        unit: '%',
        full: `${displayValue}${UNICODE.SPACES.NARROW_NON_BREAKING}%`, // percent is ok with compact notation
        raw: {
          value: input.value,
          unit: input.unit,
        },
      };
    }

    // there's a lot of weird stuff happening with mathJS. It's not possible to just chain methods, sometimes we need to create new unit-instances :(
    let mathJSUnit = mathJS.unit(input.value, input.unit);

    // divide before auto-unit selection. divide without modifying unit, so that auto unit selection isn't affected.
    const divideByValue = divideBy?.value;
    if (divideByValue !== undefined) {
      const dividedUnit = mathJSUnit.divide(mathJS.unit(divideByValue));
      if (!guards.is.number(dividedUnit)) mathJSUnit = mathJS.unit(dividedUnit);
    }
    if (guards.is.object(outputUnit) && outputUnit.unit === 'auto') {
      const unitCategory = getMathJSUnitCategory(mathJS.unit(input.unit));
      const targetUnits =
        outputUnit.autoUnits ??
        getAcceptableAutoOutputUnits(input.unit, unitCategory);

      if (targetUnits instanceof BreakPointerClass) {
        // Value must be of the same unit as the breakpoint instance
        const checkValue = mathJSUnit.to(targetUnits.breakpointUnit).toNumber();
        // Get the target unit from the breakpoint instance
        const targetUnit = targetUnits.getValue(checkValue);
        // Convert the mathJSUnit to the target unit
        mathJSUnit = mathJSUnit.to(targetUnit);
      } else if (guards.is.array(targetUnits)) {
        mathJSUnit = getCanonicalUnitInstance(mathJSUnit, unitCategory);
        mathJSUnit = mathJS.unit(mathJSUnit.valueOf()).toBest(targetUnits);
      }
    } else {
      const normalizedOutputUnit = guards.is.string(outputUnit)
        ? outputUnit
        : outputUnit.unit;
      mathJSUnit = mathJS.unit(mathJSUnit.to(normalizedOutputUnit).valueOf());
    }

    // make sure final unit contains the divideBy unit
    if (divideBy) {
      const dividedUnit = mathJSUnit.divide(mathJS.unit(divideBy.unit));
      if (!guards.is.number(dividedUnit)) mathJSUnit = mathJS.unit(dividedUnit);
    }

    const rawValue = mathJSUnit.toNumber();
    const rawUnit = mathJSUnit.formatUnits();
    const value = getDisplayNumber(rawValue, { notation });
    const unit = toDisplayUnit(rawUnit);
    return {
      value,
      valueAsNumber: rawValue,
      unit,
      full: getFullDisplayValue(value, unit, notation),
      raw: { value: rawValue, unit: rawUnit },
    };
  } catch (maybeError) {
    const error = wrapError(maybeError);
    console.error(
      `Error converting unit. Input: ${JSON.stringify(input)}. Output options: ${JSON.stringify(outputOptions)}, Error: ${error.message}`,
    );

    const value = tryCatch(
      () => getDisplayNumber(input.value, { notation }),
      `${input.value}`,
    ).value;
    const unit = toDisplayUnit(input.unit);
    return {
      value,
      valueAsNumber: input.value,
      unit,
      full: getFullDisplayValue(value, unit, notation),
      raw: { value: input.value, unit: input.unit },
      error,
    };
  }
};

/** just a small helper to get the full display value */
const getFullDisplayValue = (
  value: string,
  unit: string,
  notation: 'compact' | 'standard',
) => {
  if (notation === 'compact') return value;
  return `${value}${UNICODE.SPACES.NARROW_NON_BREAKING}${unit}`;
};

/**
 * Helper to fix the issue that mathJS.unit(10_000, 'SEK_Alias').toBest(['SEK', 'kSEK']) => "10 kSEK_Alias"
 * returns a new mathJSUnit with the same value but the canonical unit.
 *
 * this could be helpful for other units that refuse to bend as well
 *
 * Note: mathJS.unit(10_000, 'SEK_Alias').to('SEK').toBest(['SEK', 'kSEK']) does not work, for whatever reason.
 *
 * @param mathJSUnit - The mathJS unit to get the canonical unit for.
 * @param unitCategory - The unit category to get the canonical unit for.
 * @returns a new mathJSUnit with the same value but the canonical unit.
 */
const getCanonicalUnitInstance = (
  mathJSUnit: MathJSUnitInstance,
  unitCategory: UnitCategory | undefined,
): MathJSUnitInstance => {
  try {
    switch (unitCategory) {
      case UnitCategory.CurrencySEK:
        mathJSUnit = mathJS.unit(mathJSUnit.to('SEK').valueOf());
        break;
      case UnitCategory.CurrencyUSD:
        mathJSUnit = mathJS.unit(mathJSUnit.to('USD').valueOf());
        break;
      case UnitCategory.CurrencyEUR:
        mathJSUnit = mathJS.unit(mathJSUnit.to('EUR').valueOf());
        break;
      case UnitCategory.CO2e:
        mathJSUnit = mathJS.unit(mathJSUnit.to('gCO2e').valueOf());
        break;
    }
  } catch (error) {
    console.error('Error getting canonical unit', mathJSUnit.toJSON(), error);
  }
  return mathJSUnit;
};
